import { EContractStatus } from '../services/contracts/ContractService';

/** Tempo máximo aguardando conclusão do job na ATC (Assinado / Troca de carteira). */
export const CARTEIRA_AUTOMACAO_POLL_TIMEOUT_MS = 20_000;

/** Limite de segurança de iterações (evita loop infinito se o relógio falhar). */
export const CARTEIRA_AUTOMACAO_POLL_MAX_ATTEMPTS_SAFETY = 15;

/**
 * Intervalo entre polls de `carteira-automacao/ultimo-resultado` (ms).
 * Calibrado para caber em ~20s com timeout (4×500ms + depois 2s).
 */
export function carteiraAutomacaoPollIntervalMs(
  attempt: number,
  fastPoll: boolean
): number {
  if (!fastPoll) return 3000;
  if (attempt < 4) return 500;
  return 2000;
}

/**
 * Consulta ATC sem cache da API só em parte das tentativas.
 * Demais ticks usam cache de 2s no proxy GettelApi → menos HTTP/DB na ATC.
 */
export function carteiraAutomacaoPollUseFresh(
  attempt: number,
  options?: { forceFresh?: boolean }
): boolean {
  if (options?.forceFresh) return true;
  if (attempt === 0) return true;
  return attempt % 3 === 0;
}

let jsonCamelCaseToNumeric: Record<string, number> | undefined;

/** Mapa nome JSON (.NET System.Text.Json camelCase no primeiro caractere) → valor numérico do enum. */
function buildJsonCamelCaseMap(): Record<string, number> {
  if (jsonCamelCaseToNumeric) return jsonCamelCaseToNumeric;
  const map: Record<string, number> = {};
  for (const [key, value] of Object.entries(EContractStatus)) {
    if (typeof value === 'number') {
      // Se a chave for toda em maiúsculas, converte tudo para minusculas (Tratativa criada para o caso: "CVP" -> "cvp")
      const camel = key === key.toUpperCase() ? key.toLowerCase() : key.charAt(0).toLowerCase() + key.slice(1);
      map[camel] = value;
    }
  }
  jsonCamelCaseToNumeric = map;
  return map;
}

/**
 * Converte status vindo da API (número ou string enum em camelCase / PascalCase) para o valor numérico do enum.
 */
export function normalizeContractStatus(
  status: number | string | null | undefined
): number | null {
  if (status === undefined || status === null) return null;
  if (typeof status === 'number' && !Number.isNaN(status)) return status;
  if (typeof status === 'string') {
    const t = status.trim();
    if (t === '') return null;
    const asNum = Number(t);
    if (!Number.isNaN(asNum) && t !== '') return asNum;
    const map = buildJsonCamelCaseMap();
    if (map[t] !== undefined) return map[t];
    const pascal = t.charAt(0).toUpperCase() + t.slice(1);
    const fromEnum = (EContractStatus as unknown as Record<string, number>)[pascal];
    if (typeof fromEnum === 'number') return fromEnum;
  }
  return null;
}

/**
 * Retorna o texto de exibição para um status de contrato (número do enum ou string serializada pela API).
 * Fonte única da verdade para rótulos de status em toda a aplicação.
 */
export function getContractStatusText(status: number | string | null | undefined): string {
  const n = normalizeContractStatus(status);
  if (n === null) return 'Sem Status';
  switch (n) {
    // Status básicos
    case 0: return 'Criado';
    case 1: return 'Assinado';
    case 2: return 'Em Análise';
    case 3: return 'Ativo';
    case 4: return 'Suspenso';
    case 5: return 'Cancelado';
    case 6: return 'Finalizado';

    // Status intermediários do Excel
    case 10: return 'CVP';
    case 11: return 'Análise do BackOffice';
    case 12: return 'Aguardando CI';
    case 13: return 'Qualidade';
    case 14: return 'Aguardando FIDEL';
    case 15: return 'Aguardando Correção do Consultor';
    case 16: return 'Validando Documentação';
    case 17: return 'INPUT';
    case 18: return 'Aguardando Análise Anti-Fraude';
    case 19: return 'Análise do Supervisor';
    case 20: return 'Procedimento NAC';
    case 21: return 'Aguardando Documentação';
    case 22: return 'Aguardando Emissão de NF';
    case 23: return 'Aguardando Entrega';
    case 24: return 'Área de Atuação';
    case 25: return 'Reanálise de Crédito';
    case 26: return 'Aguardando Análise do Gerente de Vendas';
    case 27: return 'Procedimento Trader';
    case 28: return 'Reanálise do ADM';
    case 29: return 'Aguardando Procedimento do ADM';
    case 30: return 'Insucesso na Entrega';

    // Status finais específicos
    case 31: return 'Provisionado - Aguardando Entrega';
    case 32: return 'Processo de Ativação de Portabilidades';
    case 33: return 'Erro Portabilidade';

    // Status de aguardando assinatura
    case 34: return 'Aguardando Assinatura';

    // Status CQV - Decisão final da auditoria
    case 35: return 'Aprovado CQV';
    case 36: return 'Recusado CQV';

    // Status de envio
    case 37: return 'Enviado';

    // Status de não assinado
    case 38: return 'Não Assinado';

    // Status VTME
    case 39: return 'VTME Criado';

    case 40: return 'Recusado Qualidade';
    case 41: return 'Recusado CVP';
    case 42: return 'Troca de Carteira';
    case 62: return 'Troca de Carteira Solicitada';
    case 63: return 'Verificando Carteira';
    case 43: return 'Inserção';
    case 44: return 'Análise da Holding';
    case 45: return 'Processo de Ativação';
    case 46: return 'Aguardando Análise de Crédito pelo Gerente Business';
    case 47: return 'Aguardando Aceite Digital';
    case 48: return 'Aguardando Aprovação Customização';
    case 49: return 'Aguardando gerar P2B';
    case 50: return 'Contestação TIM';
    case 51: return 'CQV 2';
    case 52: return 'APROVAÇÃO CÓDIGO 02';
    case 53: return 'Bate/Volta';
    case 54: return 'Suporte HDC';
    case 55: return 'Cancelado (VTME)';
    case 56: return 'Reprovado';
    case 57: return 'Reprovado CQV';
    case 58: return 'Reprovado Por Crédito';
    case 59: return 'Reprovado Qualidade';
    case 60: return 'Restrição Crédito-TIM';
    case 61: return 'Troca de Carteira Não Autorizada';
    case 64: return 'Restrição Crédito — Pendências no mercado';
    case 65: return 'Rascunho';
    case 66: return 'Qualidade OK';
    case 67: return 'Aprovado CCV';
    case 68: return 'Recusado CCV';
    case 69: return 'Pedido Antigo';

    default: return String(n);
  }
}

export function isContractDraftStatus(status: number | string | null | undefined): boolean {
  return normalizeContractStatus(status) === EContractStatus.Rascunho;
}

/** Cor do `Label` (Semantic UI) alinhada à coluna de status em `ContractTable`. */
export function getContractStatusSemanticColor(
  status: number | string | null | undefined
):
  | 'red'
  | 'orange'
  | 'yellow'
  | 'olive'
  | 'green'
  | 'teal'
  | 'blue'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'brown'
  | 'grey'
  | 'black' {
  const st = normalizeContractStatus(status);
  if (st === null) return 'grey';
  if (st === EContractStatus.Ativo) return 'green';
  if (st === EContractStatus.Cancelado || st === EContractStatus.CanceladoVtme) return 'red';
  if (st === EContractStatus.Suspenso) return 'orange';
  if (st === EContractStatus.ErroPortabilidade) return 'orange';
  if (st === EContractStatus.InsucessoEntrega) return 'red';
  if (st === EContractStatus.Finalizado) return 'grey';
  if (st === EContractStatus.Assinado) return 'orange';
  if (st === EContractStatus.NaoAssinado) return 'orange';
  if (st === EContractStatus.AguardandoEmissaoNF) return 'blue';
  if (st === EContractStatus.ProvisionadoAguardandoEntrega) return 'blue';
  if (st === EContractStatus.EmAnalise) return 'yellow';
  if (st === EContractStatus.CVP) return 'yellow';
  if (st === EContractStatus.EmAnaliseBackOffice) return 'yellow';
  if (st === EContractStatus.Input) return 'yellow';
  if (st === EContractStatus.ValidandoDocumentacao) return 'yellow';
  if (
    st === EContractStatus.AprovadoCQV ||
    st === EContractStatus.QualidadeOk ||
    st === EContractStatus.AprovadoCCV
  ) {
    return 'green';
  }
  if (
    st === EContractStatus.RecusadoCQV ||
    st === EContractStatus.RecusadoQualidade ||
    st === EContractStatus.RecusadoCVP ||
    st === EContractStatus.RecusadoCCV ||
    st === EContractStatus.Reprovado ||
    st === EContractStatus.ReprovadoCqv ||
    st === EContractStatus.ReprovadoPorCredito ||
    st === EContractStatus.ReprovadoQualidade ||
    st === EContractStatus.RestricaoCreditoTim ||
    st === EContractStatus.TrocaDeCarteiraNaoAutorizada
  ) {
    return 'red';
  }
  if (st === EContractStatus.RestricaoCreditoPendenciasMercado || st === EContractStatus.ReanaliseCredito) {
    return 'orange';
  }
  if (st === EContractStatus.ProcessoDeAtivacao) return 'blue';
  if (st === EContractStatus.TrocaDeCarteiraSolicitada) return 'blue';
  if (st === EContractStatus.VerificandoCarteira) return 'teal';
  if (st === EContractStatus.TrocaDeCarteira) return 'yellow';
  if (st === EContractStatus.VtmeCriado) return 'blue';
  if (
    st === EContractStatus.AnaliseHolding ||
    st === EContractStatus.AguardandoAceiteDigital ||
    st === EContractStatus.AguardandoAnaliseCreditoGerenteBusiness ||
    st === EContractStatus.AguardandoAprovacaoCustomizacao ||
    st === EContractStatus.AguardandoGerarP2B ||
    st === EContractStatus.ContestacaoTim ||
    st === EContractStatus.Cqv2 ||
    st === EContractStatus.AprovacaoCodigo02 ||
    st === EContractStatus.BateVolta ||
    st === EContractStatus.SuporteHdc
  ) {
    return 'yellow';
  }
  if (st === EContractStatus.Criado || st === EContractStatus.Insercao) return 'teal';
  if (st === EContractStatus.Rascunho) return 'grey';
  if (st === EContractStatus.PedidoAntigo) return 'grey';
  return 'teal';
}

/** Opções para filtro de status na listagem (todos os valores do enum, ordenados). Valores em string evitam ambiguidade com Criado = 0 no Dropdown. */
export interface ContractStatusFilterOption {
  key: string;
  text: string;
  value: string;
}

export function getContractStatusFilterDropdownOptions(): ContractStatusFilterOption[] {
  const numeric = Object.entries(EContractStatus).filter(([, v]) => typeof v === 'number') as [string, number][];
  numeric.sort((a, b) => a[1] - b[1]);
  return [
    { key: 'todos', text: 'Todos os status', value: '' },
    ...numeric.map(([, val]) => ({
      key: `st-${val}`,
      text: getContractStatusText(val),
      value: String(val),
    })),
  ];
}

/**
 * Contratos (modal de linhas): troca Fastchip só com contrato em Criado ou Assinado (RF01).
 * Aceita `status` numérico ou string numérica (ex.: `"0"`) vindo da API.
 */
export function isContractStatusSwapAllowedForContractsPage(status: unknown): boolean {
  const n = normalizeContractStatus(status as number | string | null | undefined);
  if (n === null) return false;
  return n === EContractStatus.Criado || n === EContractStatus.Assinado;
}

/**
 * RN03 (UI): mesmo com bypass administrativo, não oferecer troca FAST nestes estados.
 */
export function isContractStatusAdministrativeFastchipSwapBlocked(status: unknown): boolean {
  const n = normalizeContractStatus(status as number | string | null | undefined);
  if (n === null) return true;
  return n === EContractStatus.Cancelado || n === EContractStatus.Finalizado;
}

/** Status em que o admin pode marcar o Fast Chip como enviado (regra de negócio). */
export function canMarkFastchipEnviadoForStatus(status: number | string | null | undefined): boolean {
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  return (
    n === EContractStatus.AprovadoCQV ||
    n === EContractStatus.VtmeCriado ||
    n === EContractStatus.Ativo
  );
}

/** Troca de carteira / verificação VTME em andamento (bloqueia ações conflitantes no modal). */
export function isTrocaDeCarteiraFlowStatus(status: number | string | null | undefined): boolean {
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  return (
    n === EContractStatus.TrocaDeCarteira ||
    n === EContractStatus.TrocaDeCarteiraSolicitada
  );
}

/** Qualidade/CCV já classificados — VTME Criado e aptas continuam no modal. */
export function isQualityCcvPipelineStatus(status: number | string | null | undefined): boolean {
  const n = normalizeContractStatus(status);
  return (
    n === EContractStatus.QualidadeOk ||
    n === EContractStatus.AprovadoCCV ||
    n === EContractStatus.RecusadoQualidade ||
    n === EContractStatus.RecusadoCCV
  );
}

export function isVerificandoCarteiraStatus(status: number | string | null | undefined): boolean {
  return normalizeContractStatus(status) === EContractStatus.VerificandoCarteira;
}

/**
 * Gate "Aprovado CQV ou além na esteira" para os botões de correção manual (SLA 2).
 * Lista explícita (não intervalo numérico, o enum não é sequencial por ordem de esteira).
 * Enviado(37) e NaoAssinado(38) ficam de fora — decisão cravada (sub-fluxo de assinatura, não CQV/VTME).
 */
const CONTRACT_CORRECTION_GATE_STATUSES: readonly EContractStatus[] = [
  EContractStatus.AprovadoCQV,
  EContractStatus.VtmeCriado,
  EContractStatus.RecusadoQualidade,
  EContractStatus.RecusadoCVP,
  EContractStatus.TrocaDeCarteira,
  EContractStatus.Insercao,
  EContractStatus.AnaliseHolding,
  EContractStatus.ProcessoDeAtivacao,
  EContractStatus.AguardandoAnaliseCreditoGerenteBusiness,
  EContractStatus.AguardandoAceiteDigital,
  EContractStatus.AguardandoAprovacaoCustomizacao,
  EContractStatus.AguardandoGerarP2B,
  EContractStatus.ContestacaoTim,
  EContractStatus.Cqv2,
  EContractStatus.AprovacaoCodigo02,
  EContractStatus.BateVolta,
  EContractStatus.SuporteHdc,
  EContractStatus.CanceladoVtme,
  EContractStatus.Reprovado,
  EContractStatus.ReprovadoCqv,
  EContractStatus.ReprovadoPorCredito,
  EContractStatus.ReprovadoQualidade,
  EContractStatus.RestricaoCreditoTim,
  EContractStatus.TrocaDeCarteiraNaoAutorizada,
  EContractStatus.TrocaDeCarteiraSolicitada,
  EContractStatus.VerificandoCarteira,
  EContractStatus.RestricaoCreditoPendenciasMercado,
  EContractStatus.QualidadeOk,
  EContractStatus.AprovadoCCV,
  EContractStatus.RecusadoCCV,
];

/** Botões de correção manual (SLA 2) no modal de linhas: Aprovado CQV(35) ou além na esteira, exceto Enviado(37)/NaoAssinado(38). */
export function isAprovadoCqvOrBeyondPipelineStatus(
  status: number | string | null | undefined
): boolean {
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  return (CONTRACT_CORRECTION_GATE_STATUSES as readonly number[]).includes(n);
}

/**
 * Bloco VTME da seção Qualidade: status atual é VTME Criado, ou um status
 * posterior na esteira real (espelho Excel/VTME e término Ativo/Finalizado).
 * Lista explícita — o número do enum não é a ordem. CVP (10), Qualidade (13)
 * e Ativo (3) vêm depois de VTME Criado (39). Aprovado CQV, Qualidade OK, CCV,
 * recusa de qualidade e troca de carteira ficam de fora: ainda dá para confirmar
 * VTME, ou o ramo é paralelo e não exige ter passado por VTME Criado.
 */
const VTME_CRIADO_OR_LATER_PIPELINE_STATUSES: readonly EContractStatus[] = [
  EContractStatus.VtmeCriado,
  EContractStatus.Ativo,
  EContractStatus.Finalizado,
  EContractStatus.CVP,
  EContractStatus.EmAnaliseBackOffice,
  EContractStatus.AguardandoCI,
  EContractStatus.Qualidade,
  EContractStatus.AguardandoFIDEL,
  EContractStatus.ValidandoDocumentacao,
  EContractStatus.Input,
  EContractStatus.AguardandoAnaliseAntiFraude,
  EContractStatus.AnaliseSupervisor,
  EContractStatus.ProcedimentoNAC,
  EContractStatus.AguardandoDocumentacao,
  EContractStatus.AguardandoEmissaoNF,
  EContractStatus.AguardandoEntrega,
  EContractStatus.AreaDeAtuacao,
  EContractStatus.ReanaliseCredito,
  EContractStatus.AguardandoAnaliseGerenteVendas,
  EContractStatus.ProcedimentoTrader,
  EContractStatus.ReanaliseADM,
  EContractStatus.AguardandoProcedimentoADM,
  EContractStatus.InsucessoEntrega,
  EContractStatus.ProvisionadoAguardandoEntrega,
  EContractStatus.ProcessoAtivacaoPortabilidades,
  EContractStatus.ErroPortabilidade,
  EContractStatus.RecusadoCVP,
  EContractStatus.Insercao,
  EContractStatus.AnaliseHolding,
  EContractStatus.ProcessoDeAtivacao,
  EContractStatus.AguardandoAnaliseCreditoGerenteBusiness,
  EContractStatus.AguardandoAceiteDigital,
  EContractStatus.AguardandoAprovacaoCustomizacao,
  EContractStatus.AguardandoGerarP2B,
  EContractStatus.ContestacaoTim,
  EContractStatus.Cqv2,
  EContractStatus.AprovacaoCodigo02,
  EContractStatus.BateVolta,
  EContractStatus.SuporteHdc,
  EContractStatus.CanceladoVtme,
  EContractStatus.Reprovado,
  EContractStatus.ReprovadoCqv,
  EContractStatus.ReprovadoPorCredito,
  EContractStatus.ReprovadoQualidade,
  EContractStatus.RestricaoCreditoTim,
  EContractStatus.RestricaoCreditoPendenciasMercado,
];

/** Modal de linhas: seção VTME da Qualidade só em VTME Criado ou status posterior da esteira. */
export function isVtmeCriadoOrLaterPipelineStatus(
  status: number | string | null | undefined
): boolean {
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  return (VTME_CRIADO_OR_LATER_PIPELINE_STATUSES as readonly number[]).includes(n);
}

/** Bloqueia dropdown de status na lista — alteração só pela modal (spec ajustes pós-homologação). */
export function isCarteiraListaStatusChangeBlocked(
  status: number | string | null | undefined,
  options?: { isAdmin?: boolean }
): boolean {
  if (options?.isAdmin) return false;
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  return (
    n === EContractStatus.VerificandoCarteira ||
    n === EContractStatus.TrocaDeCarteiraSolicitada
  );
}

/**
 * Status em que o PATCH parcial do modal de linhas é permitido (alinhado a
 * `ContractPatchEligibility` / `Contract.EditableStatuses` no GettelApi).
 * Exclui Aprovado CQV e demais status pós-CQV.
 */
const LINES_MODAL_INLINE_EDIT_STATUSES: readonly EContractStatus[] = [
  EContractStatus.Criado,
  EContractStatus.Assinado,
  EContractStatus.AguardandoCorrecaoConsultor,
  EContractStatus.AguardandoAssinatura,
  EContractStatus.NaoAssinado,
  EContractStatus.RecusadoCQV,
  EContractStatus.RecusadoQualidade,
  EContractStatus.RecusadoCVP,
  EContractStatus.ReprovadoCqv,
  EContractStatus.TrocaDeCarteiraSolicitada,
];

/** Admin/TL não podem alterar consultor responsável nestes status (incl. Troca de Carteira recusada). */
const RESPONSIBLE_CONSULTANT_EDIT_BLOCKED_STATUSES: readonly EContractStatus[] = [
  EContractStatus.Cancelado,
  EContractStatus.TrocaDeCarteira,
  EContractStatus.TrocaDeCarteiraSolicitada,
  EContractStatus.VerificandoCarteira,
  EContractStatus.TrocaDeCarteiraNaoAutorizada,
];

export function isResponsibleConsultantEditBlocked(status: unknown): boolean {
  const n = normalizeContractStatus(status as number | string | null | undefined);
  if (n === null) return true;
  return (RESPONSIBLE_CONSULTANT_EDIT_BLOCKED_STATUSES as readonly number[]).includes(n);
}

/**
 * Edição do consultor responsável por status e role.
 * Admin/TeamLeader: qualquer status exceto bloqueados de carteira/cancelado.
 * Manager/TeamManager: mesmos status do modal de linhas (Criado, Assinado, Recusado/Reprovado CQV).
 */
export function canEditResponsibleConsultantAtStatus(
  status: unknown,
  options: { isAdmin: boolean; isTeamLeader: boolean; isManager: boolean }
): boolean {
  if (isResponsibleConsultantEditBlocked(status)) return false;
  if (options.isAdmin || options.isTeamLeader) return true;
  if (options.isManager) return canEditLinesModalContractFields(status);
  return false;
}

/**
 * Modal de linhas: edição de responsável CQV, representantes e endereço (PATCH parcial).
 */
export function canEditLinesModalContractFields(status: unknown): boolean {
  const n = normalizeContractStatus(status as number | string | null | undefined);
  if (n === null) return false;
  return (LINES_MODAL_INLINE_EDIT_STATUSES as readonly number[]).includes(n);
}

/** Prefixo do histórico quando CQV aprovado em Troca Solicitada retorna ao status 62. */
export const TROCA_CQV_APROVADO_RETORNO_REASON_PREFIX =
  'CQV aprovado durante Troca de Carteira Solicitada';

export type ContractStatusHistoryLike = {
  status: number | string;
  previousStatus?: number | string | null;
  changeReason?: string | null;
};

/**
 * Contrato em Troca Solicitada (62) que já passou por Aprovado CQV (fluxo 62 → 35 → 62).
 * Alinhado a `ResolverUltimoStatusCqvParaSincronizacaoAtcAsync` no GettelApi.
 */
export function contractStatusHistoryIndicatesTrocaSolicitadaAfterAprovadoCqv(
  history: ContractStatusHistoryLike[]
): boolean {
  return history.some((entry) => {
    const status = normalizeContractStatus(entry.status);
    const previous = normalizeContractStatus(entry.previousStatus ?? null);
    if (status !== EContractStatus.TrocaDeCarteiraSolicitada) return false;
    if (previous !== EContractStatus.AprovadoCQV) return false;
    const reason = (entry.changeReason ?? '').trim();
    return reason.includes(TROCA_CQV_APROVADO_RETORNO_REASON_PREFIX);
  });
}

/**
 * Modal de linhas: PATCH parcial de responsável, representantes e endereço (Seller/TeamLeader).
 * Em Troca Solicitada (62), bloqueia se o contrato já passou por Aprovado CQV. Admin não usa esta regra.
 */
export function canEditLinesModalPartialFields(
  status: unknown,
  options?: { trocaSolicitadaAfterAprovadoCqv?: boolean }
): boolean {
  if (!canEditLinesModalContractFields(status)) return false;
  const n = normalizeContractStatus(status as number | string | null | undefined);
  if (
    n === EContractStatus.TrocaDeCarteiraSolicitada &&
    options?.trocaSolicitadaAfterAprovadoCqv === true
  ) {
    return false;
  }
  return true;
}

/**
 * Seller e TeamLeader: responsável, representante e endereço até antes de Aprovado CQV
 * (inclui Troca de Carteira Solicitada quando ainda não houve aprovação CQV definitiva).
 */
export function canSellerTeamLeaderEditLinesModalPartialFields(
  status: unknown,
  options?: { trocaSolicitadaAfterAprovadoCqv?: boolean }
): boolean {
  return canEditLinesModalPartialFields(status, options);
}

/** Modal de linhas: cluster/token nos mesmos status do PATCH parcial. */
export function canEditLinesModalClusterToken(status: unknown): boolean {
  return canEditLinesModalContractFields(status);
}

export type LinesModalCqvButtonsContext = {
  hasContract: boolean;
  vtmeConfirmado?: boolean;
};

/**
 * Modal de linhas (Admin): exibir Confirmar/Recusar CQV.
 * Em Troca Solicitada (62) sempre exibe CQV (mesmo com VTME já confirmado) e nunca compete com «VTME Criado».
 */
export function shouldShowLinesModalCqvButtons(
  status: number | string | null | undefined,
  { hasContract, vtmeConfirmado }: LinesModalCqvButtonsContext
): boolean {
  if (!hasContract) return false;
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  const isTrocaSolicitada = n === EContractStatus.TrocaDeCarteiraSolicitada;
  if (n === EContractStatus.AprovadoCQV) return false;
  if (n === EContractStatus.QualidadeOk) return false;
  if (n === EContractStatus.AprovadoCCV) return false;
  if (n === EContractStatus.RecusadoCCV) return false;
  if (!(isTrocaSolicitada || !isTrocaDeCarteiraFlowStatus(n))) return false;
  if (n === EContractStatus.VtmeCriado) return false;
  if (n === EContractStatus.RecusadoQualidade) return false;
  if (n === EContractStatus.RecusadoCVP) return false;
  if (isTrocaSolicitada) return true;
  return !vtmeConfirmado;
}

/**
 * Solicitar/Reenviar CQV (automação WhatsApp, specs/solicitacao-cqv-automatica-sla): só
 * enquanto o contrato está Assinado (ainda sem decisão) ou Recusado CQV (pra reenviar depois
 * de corrigir). Diferente de `shouldShowLinesModalCqvButtons` (Confirmar/Recusar CQV manual,
 * que cobre vários status intermediários do fluxo administrativo) — a automação só deve
 * disparar depois que o contrato está assinado, e some assim que for Aprovado CQV ou Cancelado.
 */
export function shouldShowSolicitarCqvButton(status: number | string | null | undefined): boolean {
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  return n === EContractStatus.Assinado || n === EContractStatus.RecusadoCQV;
}

/** Modal de linhas (Admin): «VTME Criado» — não em Troca Solicitada (62), onde prevalece CQV. */
export function shouldShowLinesModalVtmeCriadoButton(
  status: number | string | null | undefined,
  { hasContract, vtmeConfirmado }: LinesModalCqvButtonsContext
): boolean {
  if (!hasContract || vtmeConfirmado) return false;
  const n = normalizeContractStatus(status);
  if (n === null) return false;
  if (n === EContractStatus.TrocaDeCarteiraSolicitada) return false;
  return (
    n === EContractStatus.AprovadoCQV ||
    isTrocaDeCarteiraFlowStatus(n) ||
    isQualityCcvPipelineStatus(n)
  );
}

/** Modal de linhas: Qualidade OK / Recusar Qualidade — só em Aprovado CQV. */
export function shouldShowLinesModalQualidadeButtons(
  status: number | string | null | undefined
): boolean {
  return normalizeContractStatus(status) === EContractStatus.AprovadoCQV;
}

/** Modal de linhas: Aprovado CCV / Recusado CCV — só em Qualidade OK. */
export function shouldShowLinesModalCcvButtons(
  status: number | string | null | undefined
): boolean {
  return normalizeContractStatus(status) === EContractStatus.QualidadeOk;
}

export type QualityCcvFlag = {
  label: string;
  color: 'green' | 'red';
};

/** Flag extra (lista + header do modal) para Qualidade OK / Recusado Qualidade / CCV. */
export function getQualityCcvFlag(
  status: number | string | null | undefined
): QualityCcvFlag | null {
  const n = normalizeContractStatus(status);
  if (n === EContractStatus.QualidadeOk || n === EContractStatus.AprovadoCCV) {
    return { label: getContractStatusText(n), color: 'green' };
  }
  if (n === EContractStatus.RecusadoQualidade || n === EContractStatus.RecusadoCCV) {
    return { label: getContractStatusText(n), color: 'red' };
  }
  return null;
}

/** Status exibido na linha pai de um grupo (prioriza CCV / Qualidade / VTME / CQV / fluxo carteira). */
export function aggregateGroupContractStatus(
  contracts: Array<{ status?: number | string | null }>
): EContractStatus | null {
  const norms = contracts
    .map((c) => normalizeContractStatus(c.status))
    .filter((n): n is EContractStatus => n !== null);
  if (norms.length === 0) return null;
  if (norms.some((s) => s === EContractStatus.AprovadoCCV)) return EContractStatus.AprovadoCCV;
  if (norms.some((s) => s === EContractStatus.RecusadoCCV)) return EContractStatus.RecusadoCCV;
  if (norms.some((s) => s === EContractStatus.QualidadeOk)) return EContractStatus.QualidadeOk;
  if (norms.some((s) => s === EContractStatus.RecusadoQualidade)) return EContractStatus.RecusadoQualidade;
  if (norms.some((s) => s === EContractStatus.VtmeCriado)) return EContractStatus.VtmeCriado;
  if (norms.some((s) => s === EContractStatus.AprovadoCQV)) return EContractStatus.AprovadoCQV;
  if (norms.some((s) => s === EContractStatus.TrocaDeCarteiraSolicitada)) {
    return EContractStatus.TrocaDeCarteiraSolicitada;
  }
  if (norms.some((s) => s === EContractStatus.VerificandoCarteira)) {
    return EContractStatus.VerificandoCarteira;
  }
  if (norms.some((s) => s === EContractStatus.TrocaDeCarteira)) return EContractStatus.TrocaDeCarteira;
  return norms[0];
}
